import { useCallback, useEffect, useMemo, useState } from 'react'
import { SectionCard } from '../../components/card'
import { Separator } from '../../components/separator'
import { Stepper } from '../../components/stepper'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../redux/hook'
import { stepperItems } from '../../utils/shared-dropdown'
import ApplicationCooLayout from '../../layout/application-coo-layout'
import { ApplicationStep2 } from '../../models/classes/ApplicationStep2.class'
import { updateFormData } from '../../redux/slices/application-step-2-slice'
import CurrentLoadSegment from './segments/current-loan-segment'
import { LoanTypeEnum } from '../../models/enum/loan-type.enum'
import { IErrors, useForm } from '../../hooks/use-form'
import { validateFinancing } from '../../utils/validator'
import { P2Alert, P2Button, P2ButtonGroup, P2TextDescription } from 'react-bootstrap-common-script'
import { TextSubheader } from '../../components/text/text-subheader'
import FinancingForm from '../../components/HOC/financing-form/financing-form'
import useSaveDraft from '../../hooks/use-save-draft'
import { StructureStatusEnum } from '../../models/enum/structure-status.enum'
import { updateStageAStep2 } from '../../redux/slices/completed-steps-slice'
import { API_ApplicationRemove, API_HLEStatus, API_DebarmentStatus } from '../../utils/api'
import useIdleSaveDraft from '../../hooks/use-idle-save-draft'
import { PolymerAxios } from '../../utils/axios'
import { useInitialWindowScrollTo } from '../../hooks/use-initial-window-scroll-to'
import { HDBLinks } from '../../utils/shared/links'
import SaveDraftAlert from '../../components/HOC/stage-a-form/save-draft-alert'
import LeavingPageModal from './leaving-page-modal'
import SaveDraftModal from './save-draft-modal'
import BackConfirmationModal from './back-confirmation-modal'
import moment from 'moment'
import { setBackTriggered, setLastBackTriggeredTime } from '../../redux/slices/back-triggered-slice'
import { PolymerCrypto } from '../../utils/encryptor'
import { Helpers } from '../../utils/helper'
import { ApplicationStep3 } from '../../models/classes/ApplicationStep3.class'
import { ApplicationStep4 } from '../../models/classes/ApplicationStep4.class'
import { ApplicationStep5 } from '../../models/classes/ApplicationStep5.class'
import { updateFormData as updateFormDataStart } from '../../redux/slices/application-start-slice'
import { updateFormData as updateFormDataStep3 } from '../../redux/slices/application-step-3-slice'
import { updateFormData as updateFormDataStep4 } from '../../redux/slices/application-step-4-slice'
import { updateFormData as updateFormDataStep5 } from '../../redux/slices/application-step-5-slice'
import { Owner } from "../../models/polymer/Owner.model";
import { checkIsFlatResale } from '../../utils/form-helper'

let activeStep = 2;

function ApplicationCOOStep2() {

  const navigate = useNavigate();
  const AppDispatch = useAppDispatch();
  const { performSaveDraft, performSaveDraftManual } = useSaveDraft();
  useIdleSaveDraft(() => {saveDraft(false)});

  const loginUser = useAppSelector(state => state.auth.AuthenticationSlice.loginUser);
  const loginUserStructureStatus = useAppSelector(state => state.auth.AuthenticationSlice.structureStatus);
  const applicationStep1FormData = useAppSelector(state => state.app.ApplicationStep1Slice.formData);
  const applicationStep2FormData = useAppSelector(state => state.app.ApplicationStep2Slice.formData);
  const startApplicationFormData = useAppSelector(state => state.app.ApplicationStartSlice.formData);
  const applicationId = useAppSelector(state => state.app.ApplicationIdSlice.applicationId);
  const [isHLEChecking, setIsHLEChecking] = useState<boolean>(false);
  const [alertNumber, setAlertNumber] = useState<number>(0);
  const [saveDraftSuccess, setSaveDraftSuccess] = useState<boolean>(false);
  const [saveDraftModalOpen, setSaveDraftModalOpen] = useState<boolean>(false);
  const [backConfirmationModalOpen, setBackConfirmationModalOpen] = useState<boolean>(false);
  const backTriggeredSlice = useAppSelector(state => state.app.BackTriggeredSlice);
  const step_1 = useAppSelector(state => state.app.CompletedStepsSlice.stage_a.step_1);
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);
  
  const currentOwner = applicationStep1FormData?.household.owners;
  const retainingIncomingOwner = applicationStep1FormData.household.incoming_owners?.filter( owner => (owner.structure_status === StructureStatusEnum.retaining));
  const hasOutgoingOwners = retainingIncomingOwner?.length !== currentOwner?.length;

  const { values: financingValues, errors: financingErrors, formValid: financingFormValid, setFormValue: financingSetFormValue }: { handleChange: any, values: ApplicationStep2, errors: IErrors, formValid: boolean, setFormValue: (update: ApplicationStep2) => void } = useForm(new ApplicationStep2(applicationStep2FormData), () => { }, validateFinancing)

  const isNewLoanInformationRequired = useMemo(():boolean => {
    //show new loan info for some reasons, and login user is not outgoing owner if change ownership reason is divorce/marriage inclusion
    return loginUserStructureStatus !== StructureStatusEnum.outgoing
  },[loginUserStructureStatus])

  // chk debarment
  const checkDebarment = async (owner: Owner) => {
    let thereIsDebarment = false;
    const response = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(`${API_DebarmentStatus}`, {
      uinfin: PolymerCrypto.encryption(owner?.myinfo.uinfin) ?? "",
    });

    if ((response.data.debarment_code ?? []).includes("BK")) {
      thereIsDebarment = true;
    }
    return thereIsDebarment;
  };

  const checkDebarmentAndDispatch = async () => {
    const allIncomingOwners = applicationStep1FormData.household.incoming_owners;
    let result = false;

    for (const owner of allIncomingOwners) {
      result = await checkDebarment(owner);
      if (result) break;
    }

    AppDispatch(updateFormDataStart({ ...startApplicationFormData, isDebarred: result }));
  };

  useEffect(() => {
    checkDebarmentAndDispatch();
  }, []);
  
  
  useInitialWindowScrollTo();

  useEffect(()=>{
    //Init api response to form value
    financingSetFormValue({...financingValues, new_loan_type: isNewLoanInformationRequired ? financingValues.new_loan_type : undefined});
  },[isNewLoanInformationRequired])


  useEffect(()=>{
    if(!step_1){
      navigate('/application-coo/stage-a/eligibility')
    }
  },[step_1, navigate])
  
  const isValueChanged = useMemo(() => {
    let oldApplicationForm = new ApplicationStep2({...applicationStep2FormData});
    let newApplicationForm = new ApplicationStep2({...financingValues});

    return Helpers.checkIsValueChanged(oldApplicationForm, newApplicationForm);
  }, [applicationStep2FormData, financingValues])


  const navigateBack = useCallback(()=>{
    navigate("/application-coo/stage-a/eligibility")
  },[navigate])


  const fetchHleResponse = async () => {
    try{
      const proposedOwnersUinfin = applicationStep1FormData.household.incoming_owners.map(item => {
        return {
          uinfin: PolymerCrypto.encryption(item.myinfo.uinfin),
          dob: item.myinfo.dob,
        }
      });

      const proposedOccupiersUinfin = applicationStep1FormData.household.incoming_occupiers.map(item => {
        return {
          uinfin: PolymerCrypto.encryption(item.myinfo.uinfin),
          dob: item.myinfo.dob,
        }
      });
      const response = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(`${API_HLEStatus}`, {
        uinfin: PolymerCrypto.encryption(loginUser?.uinfin) ?? "",
        owners: proposedOwnersUinfin,
        occupiers: proposedOccupiersUinfin
      });
      
      if (response.data.valid_hle === false) {
        console.log("STEP 2 - hle status return false");
        setAlertNumber(2);
      } 
      else 
      {

        setAlertNumber(1);

        //NOTE : now hle no returning uinfins, so hide it for now
        // const uinfins = applicationStep1FormData.household.incoming_owners.map(
        //   (item) => item.myinfo.uinfin
        // );
        // const responseUinfins = response.data.hle_proposed_owners.map((item: any) => item.uinfin);
        
      }
    }catch(error){
        console.log(error)
    }
  };

  useEffect(()=>{
    setAlertNumber(0);
    if(financingValues.new_loan_type === LoanTypeEnum.hdb_loan){
      fetchHleResponse();
      setIsHLEChecking(true);
      setTimeout(()=>{
        setIsHLEChecking(false);
      },2000);
    }
  },[financingValues])

  useEffect(()=>{
  },[financingErrors,financingFormValid, financingValues]);

  const newLoanLogic = useMemo(():boolean=>{
    if( financingValues.new_loan_type === LoanTypeEnum.hdb_loan ){
      if(isHLEChecking === false){
        return alertNumber === 1;
      }
      return false;
    }
    else{
      return true;
    }
  },[financingValues, isHLEChecking, alertNumber]);


  const isBtnValid = useMemo(():boolean => {
    if(isNewLoanInformationRequired){
      return financingFormValid && newLoanLogic
    }else if(financingValues.current_loan_type === LoanTypeEnum.bank_loan){
      return financingErrors?.current_loan_amount?.isValid ?? true;
    }else {
        return true;
    }
  },[financingFormValid, newLoanLogic, isNewLoanInformationRequired, financingValues, financingErrors]);

  const isFlatResale = useMemo(()=>{
    return checkIsFlatResale(startApplicationFormData.selected_hdb_property?.financing_info.sales_type_code!); 
  },[startApplicationFormData.selected_hdb_property?.financing_info.sales_type_code]);


  const saveDraft = useCallback(async(requiredAlert: boolean) => {
    let response;
    if(isValueChanged){
      const newStep3Form = new ApplicationStep3();
      const newStep4Form = new ApplicationStep4();
      const newStep5Form = new ApplicationStep5();
  
      AppDispatch(updateFormDataStep3(newStep3Form));
      AppDispatch(updateFormDataStep4(newStep4Form));
      AppDispatch(updateFormDataStep5(newStep5Form));
      response = await performSaveDraftManual({applicationStart: startApplicationFormData, applicationStep1: applicationStep1FormData, applicationStep2: financingValues, applicationStep3: newStep3Form, applicationStep4: newStep4Form, applicationStep5: newStep5Form, applicationId, singpassToken});
    }else{
      response = await performSaveDraft(financingValues,2, applicationId, singpassToken);
    }
    AppDispatch(updateFormData(financingValues));
    if(requiredAlert){
      setSaveDraftSuccess(response);
    }
  }, [startApplicationFormData, applicationStep1FormData, financingValues, applicationId, singpassToken, isValueChanged, performSaveDraft, performSaveDraftManual])

  const handleSaveDraft = useCallback(() => {
    saveDraft(true);
    AppDispatch(updateFormData(financingValues));
    setSaveDraftModalOpen(false);
}, [saveDraft, financingValues])

const saveDraftLeavePage = useCallback(async()=>{
  await saveDraft(false);
},[navigate, saveDraft])

const removeApplication = useCallback(async () => {
  if(applicationId){
    try {
      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken)
        .post(`${API_ApplicationRemove}/${applicationId}`)
        .then((res) => res.data);
    } catch (error) {
      console.log(error);
    }
  }
}, [singpassToken, applicationId, loginUser]);

  const handleFinancialValueChange = useCallback((key: string) => (e: any) => {
      financingSetFormValue({...financingValues, [key]: e})
  },[financingSetFormValue, financingValues])

  const proceedNext = useCallback(() => {
    saveDraft(false).then(() => {
      AppDispatch(updateStageAStep2(true));
      navigate('/application-coo/stage-a/conveyancing')
    });
  }, [financingValues, performSaveDraft, applicationId, singpassToken, navigate, AppDispatch, isValueChanged, saveDraft]);

  const saveValuesAndGoBack = useCallback(() => {
    AppDispatch(setBackTriggered(true));
    AppDispatch(updateFormData(financingValues));
    navigateBack();
  }, [financingValues, navigateBack])

  const toggleBackConfirmationModal = useCallback(() => {
    const currentTimeString = moment().format('yyyy-MM-DD HH:mm:ss');
    const alreadyTriggered = sessionStorage.getItem("back_triggered");
    if (
      alreadyTriggered !== "true" &&
      backTriggeredSlice.is_back_triggered && 
      backConfirmationModalOpen === false &&
      (backTriggeredSlice.last_back_triggered_time === "" ||
        moment(currentTimeString).diff(moment(backTriggeredSlice.last_back_triggered_time), "seconds") >= 600)
    ) {
      sessionStorage.setItem("back_triggered", "true");
      AppDispatch(setLastBackTriggeredTime(currentTimeString));
      AppDispatch(setBackTriggered(false));
      setBackConfirmationModalOpen((prev) => !prev);
    }else{
      setBackConfirmationModalOpen(false);
    }
  }, [backConfirmationModalOpen, backTriggeredSlice]);

  useEffect(() => {
    if(saveDraftSuccess){
        setTimeout(() => {
            setSaveDraftSuccess(false);
        }, 3000)
    }
  },[saveDraftSuccess])

  const toggleSaveDraftModal = useCallback(() => {
      setSaveDraftModalOpen((prev) => !prev);
  }, [saveDraftModalOpen])
  return (
    <ApplicationCooLayout>
      <div className='polymer-application-coo__wrapper'>
        <TextSubheader 
        subheaderMain='Stage A: Start Application' 
        subheaderSub={startApplicationFormData.selected_hdb_property?.address}
        paddingLeftRight={42}
        />
        <Stepper activeStep={activeStep} items={stepperItems} style={{margin:"62px auto"}}/>
        
        <div className='d-flex justify-content-center'>
          <SectionCard 
            onClick={toggleBackConfirmationModal}
            disabled={isHLEChecking}
            className='flex-fill' 
            activeStep={activeStep}
            stepperItems={stepperItems}
            titleChildren={
              <P2TextDescription title={stepperItems[activeStep-1]?.title}/>
            } 
            contentChildren={
              <div>

                  <div className='px-4'>
                    <CurrentLoadSegment loan_type={financingValues.current_loan_type} loan_created_date={financingValues.current_loan_date} title={<P2TextDescription color='#000' title='Current Loan Information'/>} loan_outstanding_amount={financingValues.current_loan_amount} setOnCurrencyChange={handleFinancialValueChange("current_loan_amount")}/>
                  </div>
                  {isNewLoanInformationRequired && <>
                    <Separator variant='solid' style={{ margin: "48px 0 24px" }} />
                    <div className='px-4'>
                      <FinancingForm isFlatResale={isFlatResale} flat_type={startApplicationFormData.selected_hdb_property?.flat_type} change_ownership_reason={applicationStep1FormData.reason.change_ownership_reason} has_outgoing_owner={hasOutgoingOwners} current_loan_type={LoanTypeEnum.no_loan} new_loan_type={financingValues.new_loan_type} current_loan_date={financingValues.current_loan_date}
                        onLoanTypeChange={handleFinancialValueChange("new_loan_type")}
                      />
                    </div>
                  </>}
              </div>
               
            }
          />
        </div>

        <Separator style={{margin:"32px 42px"}}/>

        <div className='polymer-shared-alert-container'>
          <div style={{ marginTop: 24, marginBottom: 24 }}>
          {saveDraftSuccess && <SaveDraftAlert/>}
            {alertNumber === 1 && isHLEChecking === true && <P2Alert variant={"success"} text={"We have checked that you have a valid HDB Loan Eligibility (HLE) Letter. The maximum loan is subject to the CPF valuation limit and Loan-to-Value limit of the property, whichever is lower."}></P2Alert>}
            {alertNumber === 2 && isHLEChecking === false && <P2Alert variant={"danger"} text={<div>It appears that you have not received approval for your HLE. Please <a href = {HDBLinks.apply_hle_url} className="polymer-text-links">apply for your HLE</a> before continuing with the application. HLE application will take 2-4 weeks for processing. Your application will lapse after 4 weeks.</div>}></P2Alert>}
            {alertNumber === 3 && isHLEChecking === false && <P2Alert variant={"danger"} text={
                <>
                  <div>
                    It appears that you do not have an approved HDB Loan Eligibility (HLE) letter. Please <a href = {HDBLinks.apply_hle_url} className="polymer-text-links">apply for an HLE letter</a> first as HLE applications will take 21 working days for processing. You may continue with the application for change in flat ownership and proceed to submit once HLE have been obtained.
                  </div>
                  <div>
                    It appears that you have received approval for your HLE
                    based on a different proposed owner(s) listed in the current
                    application.
                  </div>
                  <br />
                  <div>
                    Please <a href = {HDBLinks.apply_hle_url} className="polymer-text-links">apply for your HLE</a> before continuing with the
                    application. HLE application will take 2-4 weeks for
                    processing. Your application will lapse after 4 weeks.
                  </div>
                </>
              }
            ></P2Alert>}
          </div>
        </div>

        <div className='polymer-application-coo__card-button'>
          <P2ButtonGroup>
            <P2Button onClick={saveValuesAndGoBack} variant="outline-primary">Back</P2Button><P2Button variant={"outline-primary"} onClick={toggleSaveDraftModal}>Save as draft</P2Button><P2Button onClick={proceedNext} disabled={!isBtnValid} variant="primary-1">Next</P2Button>
          </P2ButtonGroup>
        </div>

        <LeavingPageModal 
          onSubmit={saveDraftLeavePage}
          onCancel={removeApplication}
          financingValues={financingValues}
        /> 
        <SaveDraftModal onSubmit={handleSaveDraft} onCancel={removeApplication} onClose={toggleSaveDraftModal} isOpen={saveDraftModalOpen}/>
        <BackConfirmationModal onSubmit={toggleBackConfirmationModal} onClose={toggleBackConfirmationModal} isOpen={backConfirmationModalOpen}/>
      </div>
      
    </ApplicationCooLayout>

      
  )
}

export default ApplicationCOOStep2
